import {
  WHATSAPP_TEMPLATE_CATEGORIES,
  WHATSAPP_TEMPLATE_CATEGORY_LABELS,
  type WhatsappTemplateCategory,
} from '@performance-crm/shared';
import { MessageCircle, Settings2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast-context';
import { OPT_OUT_NOTICE } from '@/components/leads/QuickContactButtons';
import {
  useDeleteWhatsappTemplate,
  useOpenWhatsapp,
  useUpsertWhatsappTemplate,
  useWhatsappTemplates,
} from '@/lib/queries';

/** Botón de WhatsApp con plantillas de la empresa: abre wa.me y deja registro en el lead. Respeta el opt-out. */
export function WhatsappMenu({
  leadId,
  companyId,
  disabled,
  canManage,
  optedOut = false,
}: {
  leadId: string;
  companyId: string;
  disabled?: boolean;
  canManage: boolean;
  optedOut?: boolean;
}) {
  const { data: templates } = useWhatsappTemplates(companyId);
  const open = useOpenWhatsapp(leadId);
  const toast = useToast();
  const [manage, setManage] = useState(false);
  const send = async (templateId: string | null) => {
    try {
      const r = await open.mutateAsync({ templateId });
      window.open(r.url, '_blank', 'noopener');
    } catch (err) {
      toast.error('No se pudo abrir WhatsApp', (err as Error).message);
    }
  };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={disabled || optedOut || open.isPending}
            title={optedOut ? OPT_OUT_NOTICE : undefined}
            data-testid="btn-whatsapp"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-72">
          <DropdownMenuLabel>Plantilla</DropdownMenuLabel>
          {templates?.map((t) => (
            <DropdownMenuItem
              key={t.id}
              onSelect={() => void send(t.id)}
              className="flex-col items-start"
              data-testid="wa-template"
            >
              <span className="font-medium">{t.name}</span>
              <span className="text-muted-foreground block w-full truncate text-xs">{t.body}</span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem onSelect={() => void send(null)}>
            Sin plantilla (saludo básico)
          </DropdownMenuItem>
          {canManage && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => setManage(true)}
                className="text-muted-foreground text-xs"
              >
                <Settings2 className="h-3 w-3" /> Administrar plantillas
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {manage && <TemplatesDialog companyId={companyId} onClose={() => setManage(false)} />}
    </>
  );
}

/** Alta / baja de plantillas (también se usa en Administración). */
export function TemplatesDialog({
  companyId,
  onClose,
}: {
  companyId: string;
  onClose: () => void;
}) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Plantillas de WhatsApp</DialogTitle>
          <DialogDescription>
            Marcadores disponibles: {'{nombre}'}, {'{proyecto}'}, {'{asesor}'}, {'{empresa}'}.
          </DialogDescription>
        </DialogHeader>
        <TemplatesEditor companyId={companyId} />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TemplatesEditor({ companyId }: { companyId: string }) {
  const { data: templates } = useWhatsappTemplates(companyId);
  const upsert = useUpsertWhatsappTemplate();
  const remove = useDeleteWhatsappTemplate();
  const toast = useToast();
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState<WhatsappTemplateCategory>('UTILITY');
  const [metaName, setMetaName] = useState('');
  const [metaLanguage, setMetaLanguage] = useState('es');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await upsert.mutateAsync({
        companyId,
        name,
        body,
        category,
        metaName: metaName || null,
        metaLanguage: metaLanguage || 'es',
      });
      toast.success('Plantilla guardada');
      setName('');
      setBody('');
      setMetaName('');
    } catch (err) {
      toast.error('No se pudo guardar', (err as Error).message);
    }
  };
  return (
    <div className="space-y-3">
      <ul className="max-h-48 space-y-1 overflow-y-auto">
        {templates?.map((t) => (
          <li
            key={t.id}
            className="flex items-start justify-between gap-2 rounded border p-2 text-sm"
            data-testid="wa-template-row"
          >
            <div className="min-w-0">
              <p className="flex flex-wrap items-center gap-1 font-medium">
                {t.name}
                <Badge tone="outline">
                  {WHATSAPP_TEMPLATE_CATEGORY_LABELS[t.category].split(' (')[0]}
                </Badge>
                {t.metaName ? (
                  <Badge tone="success">Meta: {t.metaName}</Badge>
                ) : (
                  <Badge tone="muted">sin nombre en Meta</Badge>
                )}
              </p>
              <p className="text-muted-foreground text-xs">{t.body}</p>
            </div>
            <button
              type="button"
              className="text-destructive text-xs underline"
              onClick={() => remove.mutate(t.id)}
            >
              quitar
            </button>
          </li>
        ))}
        {templates?.length === 0 && (
          <li className="text-muted-foreground text-sm">Aún no hay plantillas.</li>
        )}
      </ul>
      <form onSubmit={submit} className="space-y-3 border-t pt-3">
        <div className="space-y-1">
          <Label htmlFor="tpl-name">Nombre</Label>
          <Input
            id="tpl-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={2}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="tpl-body">Texto</Label>
          <Textarea
            id="tpl-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            required
            minLength={5}
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="tpl-category">Categoría (Meta)</Label>
            <Select
              id="tpl-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as WhatsappTemplateCategory)}
            >
              {WHATSAPP_TEMPLATE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {WHATSAPP_TEMPLATE_CATEGORY_LABELS[c]}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="tpl-meta-name">Nombre en Meta (opcional)</Label>
            <Input
              id="tpl-meta-name"
              value={metaName}
              onChange={(e) => setMetaName(e.target.value)}
              placeholder="primer_contacto"
              pattern="[a-z0-9_]*"
              title="Solo minúsculas, números y guion bajo"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="tpl-meta-lang">Idioma en Meta</Label>
            <Input
              id="tpl-meta-lang"
              value={metaLanguage}
              onChange={(e) => setMetaLanguage(e.target.value)}
              placeholder="es"
              maxLength={10}
            />
          </div>
        </div>
        <p className="text-muted-foreground text-xs">
          La categoría define lo que Meta cobra por cada envío fuera de las 24 h. El nombre en Meta
          solo hace falta para el número real; en el modo simulado no se usa.
        </p>
        <div className="flex justify-end">
          <Button type="submit" size="sm" disabled={upsert.isPending}>
            Guardar plantilla
          </Button>
        </div>
      </form>
    </div>
  );
}
